import { LocalDictionaryFactory } from '../../api/api.module'
import { LoggerService } from '../../common/common.module'

import { IUserSession, EventsNames, LocalizationHelper, TemplateHelper, ConfigHelper } from '../../../helpers'

import { Activity, Enums } from '../../../models/common'
import { OpTaskType } from '../../../models/core'

import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'

import { TaskTypeHelper } from '../tasks-types.helper'


interface TaskTypeDetailsScope extends DetailsScope {
    TASK_TYPE_PARAMS_ACCORDION_NAME: string
    TASK_TYPE_COMMON_TAB_NAME: string

    id: number

    details: any

    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }
}

export class TaskTypeDetailDirectiveController extends Details<TaskTypeDetailsScope> {

    // Members

    private idTaskType: number
    private taskType: OpTaskType

    // Members End


    // Ctor

    constructor(
        $scope: TaskTypeDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        userSession: IUserSession,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, null, userSession, referenceType)

        this.setEvents()
        this.setOptions()
        this.setTemplateConsts()
        this.setConfirmationsOptions()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    // Ctor End


    // Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.idTaskType = options.referenceObjectId

        this.onClear()
        this.showPopup()
        this.onRefresh(null)
    }

    private onRefresh(toRefresh: OpTaskType = null): void {
        if (!toRefresh) {
            TaskTypeHelper
                .getTaskType(this.userSession, this.idTaskType)
                .then((taskType: OpTaskType) => {
                    this.setBasicParameters(taskType);
                    this.taskType = taskType
                    this.$scope.showTabs = true
                })
                .fail(() => {
                    this.hidePopup()
                })
        }
        else {
            this.taskType = toRefresh

            this.setBasicParameters(toRefresh);
            this.$scope.showTabs = true
        }
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        TaskTypeHelper
            .deleteTaskType(this.userSession, this.taskType)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.detailsPopupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onParamsEdit(): void {
        this.$scope.$root.$broadcast(
            EventsNames.INITIALIZE_CREATE_EDIT_POPUP,
            TaskTypeHelper.getCreateEditOptions(this.userSession, this.idTaskType, this.taskType)
        )
    }

    // Events End


    // Parameters

    private setBasicParameters(taskType) {
        this.$scope.details = TaskTypeHelper.getParmetersFields(taskType)
    }

    // Parameters End


    // Setup 

    getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.TaskTypeParameters)) {
            items.push({
                name: this.$scope.TASK_TYPE_PARAMS_ACCORDION_NAME,
                showInTab: this.$scope.TASK_TYPE_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.TaskTypeParameters),

                editMethod: () => this.onParamsEdit(),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, null, 0, items))
        }

        return accordions
    }

    getTabs(): any[] {
        return [{
            template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common), ConfigHelper.Icon.HOME, this.userSession),

            name: this.$scope.TASK_TYPE_COMMON_TAB_NAME
        }]
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.accordionsNameToShow = this.$scope.TASK_TYPE_COMMON_TAB_NAME

        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,

            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_DEL) ? Activity[Activity.TASK_TYPE_DEL] : null,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setTemplateConsts(): void {
        this.$scope.TASK_TYPE_COMMON_TAB_NAME = 'TASK_TYPE_COMMON_TAB_NAME'
        this.$scope.TASK_TYPE_PARAMS_ACCORDION_NAME = 'TASK_TYPE_PARAMS_ACCORDION_NAME'
    }

    // Setup end

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}